import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  publicReviewFormSchema,
  reviewConfigSchema,
  reviewListSchema,
  submitReviewResultSchema,
  type PublicReviewForm,
  type ReviewConfig,
  type ReviewList,
  type ReviewStatus,
  type SaveGoogleSettingsInput,
  type SaveReviewConfigInput,
  type SubmitReviewInput,
  type SubmitReviewResult,
} from '../schemas';
import { useToken } from './use-token';

/** Les avis recueillis, du plus récent au plus ancien. */
export function useReviews() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.reviews.list(),
    queryFn: () => api.get<ReviewList>('/reviews', { schema: reviewListSchema }),
    enabled: token !== null,
  });
}

/** Le suivi d'un avis (à traiter / vu / traité). */
export function useSetReviewStatus() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReviewStatus }) =>
      api.patch(`/reviews/${id}/status`, { status }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.reviews.list() }),
        // « À traiter » et les derniers avis de l'accueil en dépendent.
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]),
  });
}

/** Le formulaire du cabinet et son lien public. Écriture réservée aux admins du cabinet. */
export function useReviewConfig({ live = false }: { live?: boolean } = {}) {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.reviews.config(),
    queryFn: () => api.get<ReviewConfig>('/reviews/config', { schema: reviewConfigSchema }),
    enabled: token !== null,
    // `live` : écran de réglages partagés avec l'extension (qui peut les changer de son côté). On relit
    // au retour sur l'onglet et toutes les 15 s, sans toucher au comportement des autres écrans.
    refetchOnWindowFocus: live,
    refetchInterval: live ? 15_000 : false,
  });
}

export function useSaveReviewConfig() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveReviewConfigInput) =>
      api.put<ReviewConfig>('/reviews/config', input, { schema: reviewConfigSchema }),
    onSuccess: (config) => queryClient.setQueryData(queryKeys.reviews.config(), config),
  });
}

/** Note minimale pour Google et option « Coup de cœur » (page Réglages). */
export function useSaveGoogleSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveGoogleSettingsInput) =>
      api.put<ReviewConfig>('/reviews/google', input, { schema: reviewConfigSchema }),
    onSuccess: (config) => queryClient.setQueryData(queryKeys.reviews.config(), config),
  });
}

/** Page patient, sans session : le jeton du lien suffit. */
export function usePublicReviewForm(token: string) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.reviews.form(token),
    queryFn: () =>
      api.get<PublicReviewForm>(`/reviews/public/${token}`, { schema: publicReviewFormSchema }),
    retry: false,
  });
}

/** +1 ouverture du formulaire pour le cabinet ; sans effet sur le lien public. Une fois par montage. */
export function useCountReviewView(token: string) {
  const api = useApi();
  const counted = useRef(false);

  useEffect(() => {
    if (counted.current) return;
    counted.current = true;
    api.post(`/reviews/public/${token}/view`, {}).catch(() => {});
  }, [api, token]);
}

export function useSubmitReview(token: string) {
  const api = useApi();

  return useMutation({
    mutationFn: (input: SubmitReviewInput) =>
      api.post<SubmitReviewResult>(`/reviews/public/${token}`, input, {
        schema: submitReviewResultSchema,
      }),
  });
}

/** Note le clic du patient sur « Laisser un avis Google » ; sans effet visible, une erreur ne doit pas gêner l'ouverture de Google. */
export function useTrackGoogleClick() {
  const api = useApi();
  return (reviewId: string) => {
    api.post(`/reviews/public/review/${reviewId}/google-click`, {}).catch(() => {});
  };
}
